import { CheckCircle2, AlertTriangle, XCircle, QrCode } from "lucide-react";

const ICON = { approved: [CheckCircle2, "text-emerald-600", "Approved"], conditions: [AlertTriangle, "text-amber-600", "Approved with conditions"], declined: [XCircle, "text-red-600", "Not approved"] };

function Outdated({ list }) {
  return (
    <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="outdated-responses">
      <p className="text-xs font-semibold text-slate-600">Outdated replies — given before the case changed; they no longer count towards the decision</p>
      {list.map((r) => (
        <p key={r.role + r.date} className="text-xs text-slate-500 mt-1 line-through" data-testid="outdated-response">
          {ICON[r.outcome][2]} — Dr. {r.name} ({r.role}) · {new Date(r.date).toLocaleDateString("en-GB")}{r.fingerprint ? ` · report ${r.fingerprint}` : ""}
        </p>
      ))}
    </div>
  );
}

export function SpecialistResponses({ c }) {
  const list = c.specialist_responses || [];
  const old = c.superseded_responses || [];
  const link = c.reply_link;
  if (!list.length && !link && !old.length) return null;
  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-white p-6" data-testid="specialist-responses">
      <h4 className="font-heading font-semibold text-slate-900">Specialist replies (online)</h4>
      <p className="text-xs text-slate-500 mt-1" data-testid="report-fingerprint">Current report fingerprint: <b className="font-mono">{c.report_fingerprint}</b></p>
      {link?.superseded && (
        <p className="mt-2 text-xs rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2" data-testid="reply-link-superseded">
          The case changed after the last referral report was printed — the old QR link no longer accepts replies. Download the referral PDF again for a fresh QR code.
        </p>
      )}
      {link?.slots?.length > 1 && (
        <div className="mt-2 flex flex-wrap gap-2" data-testid="specialist-reply-progress">
          <span className="text-xs font-semibold text-slate-700">{list.length} of {link.slots.length} replies received</span>
          {link.slots.map((sl) => {
            const r = list.find((x) => x.role === sl.role);
            return <span key={sl.role} className={`text-xs px-2 py-0.5 rounded-full border ${r ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>{r ? "✓" : "⏳"} {sl.role}</span>;
          })}
        </div>
      )}
      {link && !link.closed && new Date(link.expires_at) > new Date() && (
        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5" data-testid="reply-link-info"><QrCode className="w-3.5 h-3.5" /> QR reply link active · code <b>{link.code}</b> · valid until {new Date(link.expires_at).toLocaleDateString("en-GB")}</p>
      )}
      {list.length === 0 && <p className="text-sm text-slate-500 mt-3">No reply yet.</p>}
      {old.length > 0 && <Outdated list={old} />}
      {list.map((r) => {
        const [Icon, tone, label] = ICON[r.outcome];
        return (
          <div key={r.role + r.date} className="mt-4 border-t border-slate-100 pt-4" data-testid={`specialist-response-${r.outcome}`}>
            <p className={`font-semibold flex items-center gap-2 ${tone}`}><Icon className="w-4 h-4" /> {label} — Dr. {r.name} ({r.specialty}){r.license ? ` · Lic. ${r.license}` : ""}</p>
            <p className="text-xs text-slate-500">{r.role} · {new Date(r.date).toLocaleString("en-GB")}</p>
            {r.conditions && <p className="text-sm text-slate-700 mt-1"><b>Conditions:</b> {r.conditions}</p>}
            {r.note && <p className="text-sm text-slate-700 mt-1"><b>Note:</b> {r.note}</p>}
            {r.signature && <img src={r.signature} alt="Specialist signature" className="h-14 mt-2" />}
          </div>
        );
      })}
    </div>
  );
}
